import { AuthConsumerRenderProps, withAuthContext } from "auth/AuthContext";
import axios from "axios";
import ConfirmActionModal from "components/ConfirmActionModal";
import CheckBox from "components/final-form/CheckBox";
import { DropdownOption, mapToDropdownOptionArrayWithSep } from "components/final-form/dropdownUtils";
import Input from "components/final-form/Input";
import Select from "components/final-form/Select";
import PraxFormGrid from "components/layout/PraxFormGrid";
import SearchByButtonsStyle from "components/layout/SearchByButtonsStyle";
import {
  AddEntryResponse,
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from "components/segments/entires/EntriesContext";
import ProceduresTableSegment from "components/segments/procedures/ProceduresTableSegment";
import SearchProceduresModal from "components/segments/procedures/SearchProceduresModal";
import { TreatmentsConsumerRenderProps, withTreatmentsContext } from "components/treatments/TreatmentsContext";
import { FormState } from "final-form";
import _ from "lodash";
import React, { Component } from "react";
import { Field, Form as FinalForm, FormSpy } from "react-final-form";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Grid, Header } from "semantic-ui-react";
import { getAllProcedureGroupsByTariffId } from "services/procedureGroupServices";
import { getMandateResources } from "services/resourcesServices";
import { clearProceduresSearchCache, searchForProcedures } from "services/searchProceduresServices";
import { closeTreatment } from "services/treatmentServices";
import { getEntryFavorites, toggleEntryFavorite } from "services/entryFavoriteServices";
import styled from "styled-components";
import { debounce } from "ts-debounce";
import { EntryTable, FavoriteEntryType, ProcedureCategory, SearchBy } from "ts-types/api.enums";
import {
  BaseProcedureResult,
  ResourceDto,
  SearchProcedureResultWrapper,
  SearchProceduresRequest,
  SearchProceduresResult,
  EntryFavoriteDto
} from "ts-types/api.types";
import { favoriteKey, getFavoriteEntryType, toFavoritesMap, FavoritesMap } from "utils/favoriteUtils";
import { noop } from "utils/functionUtils";
import { IndexSignature } from "utils/praxTypes";
import { findEntryPredicate } from "utils/predicateUtils";
import { formatTarmedErrorMessage, formatTarmedErrorMessages, TarmedErrorMessage } from "utils/validatorUtils";

const StyledColumn = styled(Grid.Column)`
  padding-right: 0 !important;
`;

const TableRowEqualiser = styled(Grid.Row)`
  margin-left: 15px !important;
`;

export enum Errors {
  PROCEDURE_NOT_VALID_AFTER = "procedure-is-not-valid-after"
}

interface SearchParams {
  searchTerm?: string;
  procedureType?: ProcedureCategory | number;
  searchBy?: SearchBy;
  material?: boolean;
  showSelectedOnly?: boolean;
  resourceId?: number;
}

interface FormSearchValues extends FormState {
  values: SearchParams
}

export interface IndexedSearchData extends SearchProceduresRequest, IndexSignature {
}

interface Props extends MessageSourceProps,
    EntriesConsumerRenderProps,
    TreatmentsConsumerRenderProps,
    AuthConsumerRenderProps {

  searchData: Partial<IndexedSearchData>;
  parentId: number;
  electronicDataTransfer: boolean;
  handleTreatmentClose: () => void;
  isCostEstimationComplete: boolean;
  entryTable: EntryTable;
  onUpsertTarmedProcedure: (tarmedProcedure: ExtendedBaseEntryDto) => void;
  onEditProcedure: (procedure: ExtendedBaseEntryDto) => void;
  onEditMedicament: (medicament: ExtendedBaseEntryDto) => void;
  onErrors: (errorMessage?: Array<string>) => void;
  onError: (errorMessage?: string) => void;
  setCacheSearchId: (searchId?: string) => void;
  // shows the favorite star column in the search results and allows toggling the favorite state
  favoritesEnabled?: boolean;
}

interface State {
  favorites: FavoritesMap;
  procedureTypes: Array<DropdownOption>;
  foundProcedures: Array<Partial<SearchProceduresResult>>;
  allCostEstimationProcedures: Array<Partial<SearchProceduresResult>>;
  locations: Array<ResourceDto>;
  locationOptions: Array<DropdownOption>;
  proceduresLoading: boolean;
  formSearchValues: Partial<SearchParams>;
  confirmActionModalOpen: boolean;
  searchProceduresModalOpen: boolean;
  searchId?: string;
  selectedProcedure?: number;
  searchProceduresSearchId?: string;
  requiredPageDataLoaded: boolean;
}

const excludedBaseCategories = ["PROCEDURE_GROUP", "COST_ESTIMATION_PROCEDURE", "COST_ESTIMATION_MEDICAMENT"];
const excludedBlockCategories = [...excludedBaseCategories, "COST_ESTIMATION", "BLOCK_PROCEDURE"];
const excludedCostEstimateCategories = [...excludedBaseCategories, "COST_ESTIMATION"];

class ProceduresSegment extends Component<Props, State> {

  private mounted: boolean = false;

  private cancelTokenSource = axios.CancelToken.source();

  private initialValues: Partial<SearchParams> = {
    procedureType: ProcedureCategory.BLOCK_PROCEDURE,
    searchBy: SearchBy.ALL,
    showSelectedOnly: true
  }

  constructor(props: Props) {
    super(props);

    const {
      searchData,
      isCostEstimationComplete
    } = props;

    let procedureType = ProcedureCategory.BLOCK_PROCEDURE;
    if (searchData.costEstimationId && !isCostEstimationComplete) {
      procedureType = ProcedureCategory.COST_ESTIMATION;
    } else if (props.entryTable === EntryTable.BLOCK_PROCEDURE) {
      procedureType = ProcedureCategory.PROCEDURE;
    }
    this.initialValues.procedureType = procedureType;

    this.state = {
      favorites: {},
      procedureTypes: [],
      foundProcedures: [],
      allCostEstimationProcedures: [],
      locations: [],
      locationOptions: [],
      proceduresLoading: true,
      formSearchValues: this.initialValues,
      confirmActionModalOpen: false,
      searchProceduresModalOpen: false,
      selectedProcedure: -1,
      requiredPageDataLoaded: false
    };

    this.loadRequiredPageData(props);
  }

  async loadRequiredPageData(props: Props) {
    try {

      const {
        searchData,
        entryTable,
        getMessage
      } = props;

      let shouldExclude = excludedBaseCategories;
      switch (entryTable) {
        case EntryTable.BLOCK_PROCEDURE: {
          shouldExclude = excludedBlockCategories;
          break;
        }
        case EntryTable.COST_ESTIMATION: {
          shouldExclude = excludedCostEstimateCategories;
        }
      }

      const procedureCategories: Array<DropdownOption> = Object
          .keys(ProcedureCategory)
          .filter(procedureCategory => !shouldExclude.includes(procedureCategory))
          .map(procedureCategory => {
            return {
              key: procedureCategory,
              text: getMessage(procedureCategory.replace("_", "").toLowerCase()),
              value: procedureCategory
            }
          });

      const procedureGroups = await getAllProcedureGroupsByTariffId(
          searchData.tariffId!, this.cancelTokenSource);

      const mappedProcedureGroups: Array<DropdownOption> = procedureGroups.map(procedureGroup => {
        return {
          key: procedureGroup.id,
          text: procedureGroup.description,
          value: procedureGroup.id
        }
      });

      this.setState({
        procedureTypes: procedureCategories.concat(mappedProcedureGroups)
      })

      const procedureType = this.initialValues.procedureType;
      const searchRequest: Partial<IndexedSearchData> = {
        ...searchData,
        search: "",
        searchBy: this.initialValues.searchBy,
        procedureGroupId: typeof procedureType != "string" ? procedureType : undefined,
        procedureCategory: typeof procedureType != "string" ? ProcedureCategory.BLOCK_PROCEDURE : procedureType,
        searchOnlySelected: this.initialValues.showSelectedOnly
      };

      const mandateId = props.currentUser!.userConfig.mandateId

      await this.getLocations(mandateId);

      if (props.favoritesEnabled) {
        this.loadFavorites(mandateId);
      }

      this.searchProcedures(searchRequest);

    } finally {
      this.setState({
        requiredPageDataLoaded: true
      });
    }
  }

  /** Favorites of the currently selected mandate of the user. */
  loadFavorites = (mandateId: number) => {

    getEntryFavorites(mandateId, this.cancelTokenSource)
        .then(favorites => {

          const favoritesMap = toFavoritesMap(favorites);

          // results that arrived before the favorites are sorted once the favorites are known
          this.setState(({foundProcedures}: State) => ({
            favorites: favoritesMap,
            foundProcedures: this.sortFavoritesFirst(
                foundProcedures, this.getFavoriteEntryType(this.getProcedureCategory()), favoritesMap)
          }));
        })
        .catch(noop);
  };

  /** The favorite entry type of the search results of the category, or undefined when favorites are not shown. */
  getFavoriteEntryType = (procedureCategory?: ProcedureCategory): FavoriteEntryType | undefined => {
    const {favoritesEnabled} = this.props;
    return favoritesEnabled ? getFavoriteEntryType(procedureCategory) : undefined;
  };

  getProcedureCategory = (): ProcedureCategory => {

    const {formSearchValues} = this.state;

    return typeof formSearchValues.procedureType != "string"
        ? ProcedureCategory.PROCEDURE_GROUP
        : formSearchValues.procedureType;
  };

  toggleFavorite = (procedure: Partial<SearchProceduresResult>) => {

    const {onError, getMessage, currentUser} = this.props;

    const entryType = getFavoriteEntryType(this.getProcedureCategory());

    if (!entryType || procedure.id == null) {
      return;
    }

    const entryFavorite: EntryFavoriteDto = {
      mandateId: currentUser!.userConfig.mandateId,
      entryType: entryType,
      entryId: procedure.id,
      favorite: false
    };

    toggleEntryFavorite(entryFavorite, this.cancelTokenSource)
        .then(result => {
          this.setState(({favorites}: State) => ({
            favorites: {
              ...favorites,
              [favoriteKey(result.entryType, result.entryId)]: result.favorite
            }
          }));
        })
        .catch(() => onError(getMessage("error.general")));
  };

  getLocations = async (
      mandateId: number) => {

    const resources = await getMandateResources(mandateId, this.cancelTokenSource);

    this.initialValues.resourceId = resources && resources.length > 0 ? resources[0].id : undefined;

    const locationOptions = mapToDropdownOptionArrayWithSep(
        resources, ["description"], "id");

    this.setState({
      locations: resources,
      locationOptions: locationOptions
    });
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    this.cancelTokenSource.cancel('Operation canceled by user');
  }

  componentDidUpdate(
      {
        searchData: prevSearchData,
        currentEntries: prevEntries
      }: Readonly<Props>,
      {formSearchValues: prevValues}: Readonly<State>,
      snapshot?: any
  ): void {

    const {searchData} = this.props;
    const {formSearchValues} = this.state;

    let shouldCallFilter = false;

    const searchDataKeys = Object.keys(searchData);
    for (const key of searchDataKeys) {
      if (searchData[key] != prevSearchData[key]) {
        shouldCallFilter = true;
        break;
      }
    }

    if (shouldCallFilter) {

      const searchRequest: Partial<IndexedSearchData> = {
        ...searchData,
        search: formSearchValues.searchTerm,
        searchBy: formSearchValues.searchBy,
        procedureGroupId: typeof formSearchValues.procedureType != "string" ? formSearchValues.procedureType : undefined,
        procedureCategory: typeof formSearchValues.procedureType != "string" ? ProcedureCategory.PROCEDURE_GROUP : formSearchValues.procedureType,
        searchOnlySelected: formSearchValues.showSelectedOnly
      };

      this.searchProcedures(searchRequest);

    } else if (formSearchValues.procedureType == ProcedureCategory.COST_ESTIMATION
        && prevValues.procedureType == ProcedureCategory.COST_ESTIMATION) {

      this.modifyProcedures(prevEntries);
    }
  }

  getSelectedProcedureFromChild = (selectedProcedure: number) => {

    this.setState({
      selectedProcedure: selectedProcedure
    })
  };

  modifyProcedures = (prevEntries: Array<ExtendedBaseEntryDto>) => {

    const {currentEntries} = this.props;

    const {
      foundProcedures,
      allCostEstimationProcedures
    } = this.state;

    const modifiedProcedures = Array.from(foundProcedures);
    const modifiedAllCostEstimationProcedures = Array.from(allCostEstimationProcedures);

    let arrayWasModified = false;

    modifiedProcedures.forEach((procedure, index) => {

      const foundEntry = currentEntries.find(
          currentEntry => findEntryPredicate(currentEntry, procedure));

      if (!!foundEntry && procedure.actualQuantity != undefined) {

        const actualQuantity = procedure.actualQuantity;
        const entryQuantity = foundEntry.quantity;

        let remainingQuantity = actualQuantity - entryQuantity;

        if (foundEntry.quantityFromDb) {

          const quantityFromDb = foundEntry.quantityFromDb;

          const currentEntryQty = quantityFromDb - entryQuantity;

          if (currentEntryQty < 0) {
            remainingQuantity = actualQuantity - Math.abs(currentEntryQty);
          } else {
            remainingQuantity = actualQuantity + currentEntryQty;
          }
        }

        remainingQuantity = remainingQuantity < 0 ? 0 : remainingQuantity;

        // If the entry was modified using edit/add/subtract quantity
        if (remainingQuantity != procedure.quantity) {
          modifiedProcedures[index] = {
            ...procedure,
            quantity: remainingQuantity
          };

          arrayWasModified = true;
        }

        if (!!procedure.childProcedures) {

          const {bulks = [], descendants = [], additionalEfforts = []} = foundEntry;
          const modifiedChildEntries: Array<BaseProcedureResult> = [];

          const childEntries = [...bulks, ...descendants, ...additionalEfforts];

          procedure.childProcedures.forEach(child => {

            const foundChild = childEntries.find(
                childEntry => findEntryPredicate(childEntry, child));

            if (!!foundChild) {

              let quantity = child.actualQuantity - foundChild.quantity;

              if (!!foundChild.quantityFromDb) {
                quantity += foundChild.quantityFromDb;
              }

              if (quantity != child.quantity && (child.quantity != 0 || quantity > 0)) {
                modifiedChildEntries.push({
                  ...child,
                  quantity: quantity < 0 ? 0 : quantity
                });

                arrayWasModified = true;
              } else {
                modifiedChildEntries.push({...child});
              }
            } else if (childEntries.length != procedure.childProcedures!.length) {

              const foundPrevEntry = prevEntries.find(
                  prevEntry => findEntryPredicate(prevEntry, procedure));

              if (!!foundPrevEntry) {
                if (foundPrevEntry.quantityFromDb) {

                  const {
                    bulks: prevBulks = [],
                    descendants: prevDescendants = [],
                    additionalEfforts: prevAdditionalEfforts = []
                  } = foundPrevEntry;

                  const prevChildEntries =
                      [...prevBulks, ...prevDescendants, ...prevAdditionalEfforts];

                  const foundPrevChild = prevChildEntries.find(
                      prevChild => findEntryPredicate(prevChild, child));

                  if (!!foundPrevChild) {

                    const calculatedQuantity = !!foundPrevChild.quantityFromDb ?
                        child.actualQuantity + foundPrevChild.quantityFromDb : child.actualQuantity;

                    modifiedChildEntries.push({
                      ...child,
                      quantity: calculatedQuantity,
                      actualQuantity: calculatedQuantity
                    });

                    arrayWasModified = true;
                  } else {
                    modifiedChildEntries.push({...child});
                  }

                } else if (child.actualQuantity != child.quantity) {
                  modifiedChildEntries.push({
                    ...child,
                    quantity: child.actualQuantity
                  });

                  arrayWasModified = true;
                } else {
                  modifiedChildEntries.push({...child});
                }
              } else {
                modifiedChildEntries.push({...child});
              }
            } else {
              modifiedChildEntries.push({...child});
            }
          });

          if (arrayWasModified) {
            modifiedProcedures[index] = {
              ...modifiedProcedures[index],
              childProcedures: modifiedChildEntries
            };
          }
        }
      } else if (prevEntries.length != currentEntries.length) {

        const prevEntry = prevEntries.find(
            prevEntry => findEntryPredicate(prevEntry, procedure));

        if (!!prevEntry && prevEntry.quantityFromDb) {

          let modifiedChildProcedures: Array<BaseProcedureResult> = [];

          if (!!procedure.childProcedures) {

            const {bulks = [], descendants = [], additionalEfforts = []} = prevEntry;

            const prevChildEntries = [...bulks, ...descendants, ...additionalEfforts];

            procedure.childProcedures.forEach(childProcedure => {

              const foundPrevChild = prevChildEntries.find(
                  prevChild => findEntryPredicate(prevChild, childProcedure));

              if (!!foundPrevChild) {

                const calculatedQuantity = !!foundPrevChild.quantityFromDb ?
                    childProcedure.actualQuantity + foundPrevChild.quantityFromDb : childProcedure.actualQuantity;

                modifiedChildProcedures.push({
                  ...childProcedure,
                  quantity: calculatedQuantity,
                  actualQuantity: calculatedQuantity
                })
              } else {
                modifiedChildProcedures.push({
                  ...childProcedure,
                  quantity: childProcedure.actualQuantity!
                })
              }
            });
          }

          // If the edited entry was removed
          modifiedProcedures[index] = {
            ...procedure,
            quantity: procedure.actualQuantity! + prevEntry.quantityFromDb,
            actualQuantity: procedure.actualQuantity! + prevEntry.quantityFromDb,
            childProcedures: modifiedChildProcedures
          };

          arrayWasModified = true;
        } else if (!!prevEntry) {

          let modifiedChildProcedures: Array<BaseProcedureResult> = [];

          if (!!procedure.childProcedures) {
            procedure.childProcedures.forEach(childProcedure => {
              modifiedChildProcedures.push({
                ...childProcedure,
                quantity: childProcedure.actualQuantity!
              })
            });
          }

          // If the entry was removed
          modifiedProcedures[index] = {
            ...procedure,
            quantity: procedure.actualQuantity,
            childProcedures: modifiedChildProcedures
          };

          arrayWasModified = true;
        }
      }

      if (arrayWasModified) {
        const foundCostEstimationIndex = modifiedAllCostEstimationProcedures
            .findIndex(costEstimation => costEstimation.id == procedure.id);

        if (foundCostEstimationIndex != -1) {
          modifiedAllCostEstimationProcedures[foundCostEstimationIndex] = {
            ...modifiedAllCostEstimationProcedures[foundCostEstimationIndex],
            quantity: modifiedProcedures[index].quantity,
            actualQuantity: modifiedProcedures[index].actualQuantity,
            childProcedures: modifiedProcedures[index].childProcedures
          }
        }
      }
    });

    if (arrayWasModified) {
      this.checkForTreatmentClosure(modifiedAllCostEstimationProcedures);
      this.setState({
        foundProcedures: modifiedProcedures,
        allCostEstimationProcedures: modifiedAllCostEstimationProcedures
      })
    }
  };

  checkForTreatmentClosure(costEstimationEntries: Array<Partial<SearchProceduresResult>>) {

    const flattenedEntries: Array<any> = [];

    costEstimationEntries.forEach(entry => {
      flattenedEntries.push(entry);

      if (!!entry.childProcedures) {
        flattenedEntries.push(...entry.childProcedures);
      }
    });

    const summedQuantity = flattenedEntries
        .map(costEstimation => costEstimation.quantity)
        .reduce((costEstimation1 = 0, costEstimation2 = 0) => costEstimation1 + costEstimation2, 0);

    if (summedQuantity == 0) {
      this.toggleConfirmActionModal();
    }
  }

  toggleConfirmActionModal = () => {
    this.setState(({confirmActionModalOpen}: State) => ({
      confirmActionModalOpen: !confirmActionModalOpen
    }))
  };

  onOpenSearchProceduresModal = () => {
    this.setState(() => ({
      searchProceduresModalOpen: true
    }));
  };

  onCloseSearchProceduresModal = () => {
    const {searchProceduresSearchId} = this.state;

    if (searchProceduresSearchId) {
      clearProceduresSearchCache(searchProceduresSearchId, this.cancelTokenSource)
          .then(response => response)
          .catch(noop);
    }

    this.setState(() => ({
      searchProceduresSearchId: undefined,
      searchProceduresModalOpen: false
    }));
  };

  setSearchProceduresSearchId = (searchProceduresSearchId?: string): void => {
    this.setState({
      searchProceduresSearchId
    })
  };

  closeTreatment = () => {

    const {handleTreatmentClose, selectedTreatment, editTreatment} = this.props;

    handleTreatmentClose();

    closeTreatment(selectedTreatment!.id, this.cancelTokenSource)
        .then(response => editTreatment(response)).catch(noop);
  };

  onSelectProcedure = (procedure: Partial<SearchProceduresResult>) => {

    const {
      addEntry,
      searchData,
      getMessage,
      onUpsertTarmedProcedure,
      onEditProcedure,
      onEditMedicament,
      parentId,
      entryTable,
      electronicDataTransfer,
      onError,
      onErrors,
      getMessageWithNamedParams
    } = this.props;

    const {
      foundProcedures,
      formSearchValues
    } = this.state;

    const procedureCategory = typeof formSearchValues.procedureType != "string"
        ? ProcedureCategory.PROCEDURE_GROUP
        : formSearchValues.procedureType;

    const result = addEntry(procedure, procedureCategory, parentId, electronicDataTransfer, searchData, entryTable);

    if (result) {
      if ((result as Promise<AddEntryResponse | undefined>).then) {
        (result as Promise<AddEntryResponse | undefined>)
            .then(response => {
              if (response) {

                if (response.entry) {
                  if (response.entry.tarmedProcedure) {
                    onUpsertTarmedProcedure(response.entry);
                  } else if (!!response.entry.procedureId) {
                    onEditProcedure(response.entry);
                  } else if (!!response.entry.medicamentId) {
                    onEditMedicament(response.entry);
                  }
                } else if (response.errorMessages && response.errorMessages.length > 0) {
                  onErrors(formatTarmedErrorMessages(response.errorMessages, getMessage, getMessageWithNamedParams));
                }

                if (procedureCategory == ProcedureCategory.COST_ESTIMATION) {
                  const foundProcedureIndex = foundProcedures
                      .findIndex((foundProcedure: Partial<SearchProceduresResult>) => foundProcedure.id == procedure.id);

                  if (foundProcedureIndex != -1) {

                    const foundProcedure = foundProcedures[foundProcedureIndex];

                    if (foundProcedure.quantity && foundProcedure.quantity > 0) {

                      const modifiedArray = Array.from(foundProcedures);

                      modifiedArray[foundProcedureIndex] = {
                        ...foundProcedure,
                        quantity: foundProcedure.quantity - 1
                      };

                      this.setState({
                        foundProcedures: modifiedArray
                      })
                    }
                  }
                }
              }
            })
            .catch(err => {

              const data = err && err.response ? err.response.data : undefined;

              if (!!data && !!data.violations) {

                const {violations} = data;

                const violation = violations[0];

                switch (violation.errorCode) {
                  case Errors.PROCEDURE_NOT_VALID_AFTER: {
                    onError(violation.details);
                    break;
                  }
                  default: {
                    onError(getMessage("error.general"));
                  }
                }
              }
            });
      } else {
        onError(formatTarmedErrorMessage(result as TarmedErrorMessage, getMessage, getMessageWithNamedParams));
      }
    }
  };

  searchProcedures = debounce((searchRequest: Partial<IndexedSearchData>) => {

    if (!this.mounted) {
      return;
    }

    const {searchId} = this.state;
    const {calculateCostEstimationQuantities, setCacheSearchId} = this.props;

    this.setState({
      proceduresLoading: true
    });

    searchRequest.searchId = searchId;

    searchForProcedures(searchRequest, this.cancelTokenSource)
        .then((response: SearchProcedureResultWrapper) => {

          const {searchProcedureResults, searchId} = response;

          if (searchRequest.procedureCategory == ProcedureCategory.COST_ESTIMATION) {

            const {allCostEstimationProcedures} = this.state;
            const modifiedProcedures = calculateCostEstimationQuantities(searchProcedureResults);

            this.setState({
              proceduresLoading: false,
              foundProcedures: modifiedProcedures,
              allCostEstimationProcedures: ["", undefined].includes(searchRequest.search) ? modifiedProcedures : allCostEstimationProcedures,
              searchId: searchId
            })
          } else {
            // favorites are sorted first when the results arrive; toggling a favorite afterwards
            // keeps the entry in place until the next search
            const favoriteEntryType = this.getFavoriteEntryType(
                searchRequest.procedureCategory as ProcedureCategory | undefined);

            this.setState({
              proceduresLoading: false,
              foundProcedures: this.sortFavoritesFirst(searchProcedureResults, favoriteEntryType),
              searchId: searchId
            })
          }

          setCacheSearchId(searchId);
        })
        .catch(() => this.setState({proceduresLoading: false, foundProcedures: []}));
  }, 400);

  changeSearchByTo = (searchBy: SearchBy) => {
    this.setState(({formSearchValues}: State) => ({
      formSearchValues: {
        ...formSearchValues,
        searchBy: searchBy,
        searchTerm: ""
      }
    }))
  };

  disableSearchBy = (procedureType?: ProcedureCategory): boolean => {
    return procedureType ?
        [ProcedureCategory.BLOCK_PROCEDURE, ProcedureCategory.COST_ESTIMATION].includes(procedureType) : false;
  };

  handleChange = ({values}: FormSearchValues) => {

    if (!this.mounted) {
      return;
    }

    const {searchData, currentUser} = this.props;

    const searchRequest: Partial<IndexedSearchData> = {
      ...searchData,
      search: values.searchTerm,
      searchBy: values.searchBy,
      procedureGroupId: typeof values.procedureType != "string" ? values.procedureType : undefined,
      procedureCategory: typeof values.procedureType != "string" ? ProcedureCategory.PROCEDURE_GROUP : values.procedureType,
      searchOnlySelected: values.showSelectedOnly,
      mandateId: currentUser!.userConfig.mandateId,
      resourceId: values.resourceId
    };
    this.searchProcedures(searchRequest);

    this.setState({
      formSearchValues: values
    })
  };

  render(): React.ReactNode {

    const {
      requiredPageDataLoaded
    } = this.state;

    if (!requiredPageDataLoaded) {
      return <></>;
    }

    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true, values: true}}
            render={this.renderForm}
            initialValues={this.initialValues}
        />
    );
  }

  /**
   * Favorites first, the remaining results keep the order of the search response (stable sort).
   * Without a favorite entry type the results are returned unchanged.
   */
  sortFavoritesFirst = (
      procedures: Array<Partial<SearchProceduresResult>>,
      favoriteEntryType?: FavoriteEntryType,
      favorites: FavoritesMap = this.state.favorites): Array<Partial<SearchProceduresResult>> => {

    if (!favoriteEntryType) {
      return procedures;
    }

    const isFavorite = (procedure: Partial<SearchProceduresResult>): boolean =>
        procedure.id != null && !!favorites[favoriteKey(favoriteEntryType, procedure.id)];

    return _.sortBy(procedures, procedure => isFavorite(procedure) ? 0 : 1);
  };

  renderForm = ({values}: FormState): React.ReactNode => {

    const {procedureType} = values;
    const {searchData, getMessage} = this.props;
    const {
      procedureTypes,
      selectedProcedure,
      formSearchValues: {searchBy},
      proceduresLoading,
      foundProcedures,
      confirmActionModalOpen,
      searchProceduresModalOpen,
      locationOptions
    } = this.state;

    const procedureCategory: ProcedureCategory = typeof procedureType == "object" ?
        procedureType.procedureCategory : procedureType;

    const data = {
      ...searchData,
      procedureCategory: ProcedureCategory.PROCEDURE
    };

    const markedProcedure = foundProcedures.find(procedure => procedure.id === selectedProcedure);

    const favoriteEntryType = this.getFavoriteEntryType(procedureCategory);

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">{getMessage("procedures")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <StyledColumn width={4} verticalAlign={"middle"}>
              {`${getMessage("procedure")} ${getMessage("type").toLowerCase()}:`}
            </StyledColumn>
            <Grid.Column width={12} verticalAlign={"middle"}>
              <Field
                  name="procedureType"
                  component={Select}
                  options={procedureTypes}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign="middle">
              {getMessage("location.title")}:
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="resourceId"
                  component={Select}
                  options={locationOptions}
                  disabled={![ProcedureCategory.MEDICAMENT, ProcedureCategory.MATERIAL].includes(procedureType)}
                  clearable
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign={"middle"}>
              {`${getMessage("patient.search.label")}:`}
            </Grid.Column>
            <Grid.Column width={6} verticalAlign={"middle"}>
              <Field
                  name="searchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
            <Grid.Column width={6} textAlign={'right'}>
              <SearchByButtonsStyle compact size="tiny">
                <Button
                    compact
                    primary={searchBy == SearchBy.CODE}
                    basic={searchBy != SearchBy.CODE}
                    onClick={() => this.changeSearchByTo(SearchBy.CODE)}
                    disabled={this.disableSearchBy(procedureCategory)}
                    type={"button"}
                >
                  {getMessage("code")}
                </Button>
                <Button
                    compact
                    primary={searchBy == SearchBy.DESCRIPTION}
                    basic={searchBy != SearchBy.DESCRIPTION}
                    onClick={() => this.changeSearchByTo(SearchBy.DESCRIPTION)}
                    disabled={this.disableSearchBy(procedureCategory)}
                    type={"button"}
                >
                  {getMessage("description")}
                </Button>
                <Button
                    compact
                    primary={searchBy == SearchBy.ALL}
                    basic={searchBy != SearchBy.ALL}
                    onClick={() => this.changeSearchByTo(SearchBy.ALL)}
                    disabled={this.disableSearchBy(procedureCategory)}
                    type={"button"}
                >
                  {getMessage("all")}
                </Button>
              </SearchByButtonsStyle>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}></Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="showSelectedOnly"
                  component={CheckBox}
                  label={getMessage('patient.showSelected.checkbox')}
              />
            </Grid.Column>
          </Grid.Row>
          <TableRowEqualiser>
            <ProceduresTableSegment
                proceduresLoading={proceduresLoading}
                tariffId={searchData.tariffId}
                date={searchData.validDate}
                procedureCategory={procedureCategory}
                procedures={foundProcedures}
                setSelectedProcedure={this.getSelectedProcedureFromChild}
                onSelectProcedure={this.onSelectProcedure}
                favoriteEntryType={favoriteEntryType}
                favorites={this.state.favorites}
                onToggleFavorite={this.toggleFavorite}
            />
            <ConfirmActionModal
                messageHeader={getMessage("treatment.close")}
                message={getMessage("costEstimation.complete")}
                modalOpen={confirmActionModalOpen}
                onCancelAction={this.toggleConfirmActionModal}
                onConfirmAction={this.closeTreatment}
            />
            <SearchProceduresModal
                modalOpen={searchProceduresModalOpen}
                onCloseModal={this.onCloseSearchProceduresModal}
                onSelectProcedure={this.onSelectProcedure}
                setSearchProceduresSearchId={this.setSearchProceduresSearchId}
                searchData={data}
            />
          </TableRowEqualiser>
          <Grid.Row>
            <Grid.Column width={9} textAlign="left">
              <Button compact type="button" primary
                      onClick={markedProcedure ? () => this.onSelectProcedure(markedProcedure) : noop}
              >
                {getMessage("procedure.selected.add")}
              </Button>
            </Grid.Column>
            <Grid.Column width={7} textAlign="right">
              <Button compact type="button" primary onClick={this.onOpenSearchProceduresModal}>
                {getMessage("search.procedures")}
              </Button>
            </Grid.Column>
          </Grid.Row>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PraxFormGrid>
    )
  };
}

const ProceduresSegmentWrapper =
    withMessages(
        withEntriesContext(
            withTreatmentsContext(
                withAuthContext(
                    ProceduresSegment))));

export default ProceduresSegmentWrapper;
